import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Link,
  Divider,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Star as ProIcon,
  Policy as PolicyIcon,
  Gavel as TermsIcon,
  PhotoCamera as PhotoCameraIcon,
  Mic as MicIcon,
  Sensors as NotificationsIcon,
  Person as PersonIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowBack as ArrowBackIcon,
  LightbulbOutlined as TipIcon,
} from '@mui/icons-material';
import {
  HomeIcon,
  DocumentTextIcon,
  BookOpenIcon,
  BeakerIcon,
  DocumentChartBarIcon,
  HeartIcon,
  UserCircleIcon,
  PresentationChartLineIcon,
  UsersIcon,
  BoltIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import BottomNavigation from './BottomNavigation';
import { useTier } from '../hooks/useTier';
import { Link as RouterLink } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { AIBarProvider } from '../contexts/AIBarContext';
import PersistentAIBar from './PersistentAIBar';
import NotificationCenter from './NotificationCenter';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import OfflineBanner from './OfflineBanner';
import EmailVerificationBanner from './EmailVerificationBanner';
import { externalLinks, openExternalLink } from '../utils/externalLinks';
import { useAutoSupplements } from '../hooks/useAutoSupplements';
import { useHealthKitAutoSync } from '../hooks/useHealthKitAutoSync';
import { useWidgetDeepLink } from '../hooks/useWidgetDeepLink';
import { usePushRegistration } from '../hooks/usePushRegistration';
import { useWidgetDiscovery } from '../hooks/useWidgetDiscovery';
import WidgetDiscoveryDialog from './WidgetDiscoveryDialog';
import TrialPaywall from './onboarding/TrialPaywall';
import Txt from './design-system/Txt';
import { INK, MONO } from './instrument';

import { apiService } from '../services/api';
import { trackOnboarding } from '../utils/onboardingTracking';
import { track } from '../utils/track';
import { sendAppleAdsAttribution } from '../utils/attribution';

/** Screens the free-week offer never interrupts. */
const OFFER_SKIP_PATHS = ['/scan', '/upgrade', '/onboarding', '/login', '/signup', '/reset-password'];

interface GlobalLayoutProps {
  children?: React.ReactNode;
}

// Sidebar (md and up). Same order as the phone tab bar first (Home, Food, Analysis, Exercise, Labs),
// then Coach (a header icon on phones), then the pages that open from Home cards on a phone.
const navigationItems = [
  { path: '/dashboard', label: 'Home', icon: HomeIcon },
  { path: '/food-log', label: 'Food Log', icon: DocumentTextIcon },
  { path: '/analysis', label: 'Analysis', icon: PresentationChartLineIcon },
  { path: '/exercise-log', label: 'Exercise', icon: BoltIcon },
  { path: '/supplements', label: 'Supplements', icon: BeakerIcon },
  { path: '/bloodwork', label: 'Labs', icon: DocumentChartBarIcon },
  { path: '/coach', label: 'Coach', icon: ChatBubbleBottomCenterTextIcon },
  { path: '/fitness', label: 'Recovery', icon: HeartIcon, divider: true },
  { path: '/my-foods', label: 'My Foods', icon: BookOpenIcon },
  { path: '/friends', label: 'Friends', icon: UsersIcon },
  { path: '/profile', label: 'Profile', icon: UserCircleIcon },
];

export const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [shouldOpenAI, setShouldOpenAI] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { unreadCount, markAllAsRead, pendingFriendRequests } = useNotificationsContext();


  // Add click-away listener for custom menu
  React.useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (profileMenuAnchor && !isNavigating) {
        const target = event.target as Node;
        // Check if click is outside the menu and not on the profile button
        if (!profileMenuAnchor.contains(target)) {
          const menuElement = document.querySelector('[data-custom-profile-menu]');
          if (menuElement && !menuElement.contains(target)) {

            setProfileMenuAnchor(null);
          }
        }
      }
    };

    if (profileMenuAnchor) {
      document.addEventListener('mousedown', handleClickAway);
      return () => document.removeEventListener('mousedown', handleClickAway);
    }
  }, [profileMenuAnchor, isNavigating]);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Close all drawers/overlays on route change to prevent stuck backdrops
  // Also scroll to top — no native scroll restoration in this SPA
  React.useEffect(() => {
    setMobileDrawerOpen(false);
    setNotificationDrawerOpen(false);
    setProfileMenuAnchor(null);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { isPro, tier } = useTier();
  useAutoSupplements();
  useHealthKitAutoSync();
  useWidgetDeepLink();
  usePushRegistration();
  const widgetDiscovery = useWidgetDiscovery();

  // Free-week offer: shown once, the first time a non-subscriber opens the app after
  // their Free Pro Day ends (usually day 2). Dismissible — never blocks the app.
  // Guarded by a server flag (trialPaywallShownAt) plus a per-session ref.
  // 2026-10-11: no longer waits for the 3-day setup checklist or 2 days of food —
  // free photo logging runs out within a day or two of the Pro day, so a day-3 offer
  // arrived after the photo wall (Bartek, Poland, hit the wall before the offer).
  const [showTrialPaywall, setShowTrialPaywall] = React.useState(false);
  const paywallEvaluatedRef = React.useRef(false);
  React.useEffect(() => {
    if (paywallEvaluatedRef.current) return;
    if (!user?._id) return;
    // Not in the middle of a scan, a purchase or sign-in.
    if (OFFER_SKIP_PATHS.some((p) => location.pathname.startsWith(p))) return;
    // Only free (non-comped) users
    if (isPro || tier === 'pro') return;
    if ((user as any).trialPaywallShownAt) return;
    // Only after the Free Pro Day has actually ended
    const expiry = (user as any).freeProDayExpiresAt;
    if (!expiry || new Date(expiry).getTime() > Date.now()) return;
    // Only for a Pro day that ended recently — 'your Pro day is over' would be wrong for a months-old account.
    if (Date.now() - new Date(expiry).getTime() > 14 * 24 * 60 * 60 * 1000) return;
    paywallEvaluatedRef.current = true;
    trackOnboarding('paywall_view');
    setShowTrialPaywall(true);
  }, [user, location.pathname, isPro, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissTrialPaywall = React.useCallback(() => {
    setShowTrialPaywall(false);
    apiService.post('/auth/trial-paywall-shown', {}).catch(() => {});
  }, []);

  // Emit one session_start per app session for true DAU / D1-D7-D30 retention
  // (previously retention was proxied through FoodLog only). Guarded per session.
  React.useEffect(() => {
    if (!user?._id) return;
    if (sessionStorage.getItem('ft_session_started')) return;
    sessionStorage.setItem('ft_session_started', '1');
    track('session_start');
    sendAppleAdsAttribution();
  }, [user?._id]);

  // Show a "Back to Setup" bar on activation action pages while a new user is
  // still mid-setup (not graduated, account < 3 days). One tap returns to the
  // activation hub, which auto-refreshes completed steps on focus.
  const ACTIVATION_ACTION_PATHS = ['/dashboard', '/food-log', '/scan', '/supplements', '/bloodwork', '/profile', '/exercise-log'];
  const showSetupBar = (() => {
    if (!user?._id) return false;
    if (localStorage.getItem(`fueltron_activation_graduated_${user._id}`) === 'true') return false;
    const createdMs = (user as any).createdAt ? new Date((user as any).createdAt).getTime() : 0;
    const isNew = createdMs > 0 && (Date.now() - createdMs) < 3 * 24 * 60 * 60 * 1000;
    if (!isNew) return false;
    return ACTIVATION_ACTION_PATHS.some((p) => location.pathname.startsWith(p));
  })();

  const handleCameraAction = () => {
    // Navigate to food log and trigger camera
    navigate('/food-log');
    // Trigger camera functionality after a short delay to ensure page is loaded
    setTimeout(() => {
      const cameraEvent = new CustomEvent('triggerCamera');
      window.dispatchEvent(cameraEvent);
    }, 300);
  };

  const handleVoiceAction = () => {
    // Navigate to food log and trigger voice
    navigate('/food-log');
    // Trigger voice functionality after a short delay to ensure page is loaded
    setTimeout(() => {
      const voiceEvent = new CustomEvent('triggerVoice');
      window.dispatchEvent(voiceEvent);
    }, 300);
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isNavigating) return;
    
    try {
      setIsNavigating(true);
      await logout();
      navigate('/landing');
      setTimeout(() => {
        setProfileMenuAnchor(null);
        setIsNavigating(false);
      }, 100);
    } catch (error) {
      console.error('Logout failed:', error);
      setIsNavigating(false);
    }
  };

  const getCurrentPageTitle = () => {
    const currentItem = navigationItems.find((item) => location.pathname.startsWith(item.path));
    return currentItem?.label || 'FuelTron';
  };

  const navButtonSx = (isActive: boolean) => ({
    position: 'relative' as const,
    color: isActive ? INK.cyan : 'rgba(255,255,255,0.7)',
    backgroundColor: isActive ? 'rgba(0,224,255,0.1)' : 'transparent',
    borderRadius: '8px',
    minHeight: 40,
    pl: 1.5,
    pr: 1,
    py: 0.5,
    overflow: 'hidden',
    '&::before': isActive ? {
      content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 2, borderRadius: 1,
      background: INK.cyan, boxShadow: `0 0 8px ${INK.cyan}, 0 0 2px ${INK.cyan}`,
    } : {},
    '&:hover': {
      backgroundColor: isActive ? 'rgba(0,224,255,0.14)' : 'rgba(0,224,255,0.06)',
      color: isActive ? INK.cyan : INK.text,
    },
  });

  const drawerContent = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          minHeight: { xs: 56, sm: 64 },
          borderBottom: '1px solid rgba(0,224,255,0.12)',
        }}
      >
        <img
          src="/FuelTron.png"
          alt="FuelTron Logo"
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))',
            border: '1px solid rgba(0,224,255,0.3)',
          }}
        />
        <Box
          component="span"
          sx={{
            fontFamily: MONO,
            fontWeight: 800,
            fontSize: '1rem',
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            background: `linear-gradient(135deg, ${INK.cyan}, ${INK.green})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 8px rgba(0,224,255,0.35))',
            display: 'inline-flex',
            alignItems: 'center',
            pr: '3px',
          }}
        >
          FuelTron
        </Box>
      </Box>
      <Box sx={{ px: 2, pt: 1.25, display: 'flex' }}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            px: 0.75, py: 0.25, borderRadius: '4px',
            color: isPro ? INK.cyan : INK.muted,
            background: isPro ? `${INK.cyan}1F` : 'rgba(255,255,255,0.06)',
            '& svg': { fontSize: 12 },
          }}
        >
          {isPro && <ProIcon />}
          {`${(tier || 'free').charAt(0).toUpperCase() + (tier || 'free').slice(1)} Tier`}
        </Box>
      </Box>
      {!isPro && (
        <Box sx={{ px: 1.5, pt: 1 }}>
          <Button
            component={RouterLink}
            to="/upgrade"
            variant="contained"
            color="primary"
            size="small"
            fullWidth
          >
            Upgrade to Pro
          </Button>
        </Box>
      )}
      <List sx={{ px: 1.5, pt: 1.5 }}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path) || (item.path === '/dashboard' && location.pathname === '/');
          return (
            <ListItem key={item.path} disablePadding sx={{ display: 'block', mb: 0.25, ...((item as any).divider ? { mt: 1, pt: 1, borderTop: '1px solid rgba(0,224,255,0.12)' } : {}) }}>
              {(item as any).divider && (
                <Txt sx={{ px: 1.5, pt: 0.25, pb: 0.75, fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
                  More
                </Txt>
              )}
              <ListItemButton
                onClick={() => {
                  if (isMobile && mobileDrawerOpen) {
                    setMobileDrawerOpen(false);
                    // Navigate after backdrop dismisses to prevent stuck blur layer
                    setTimeout(() => navigate(item.path), 200);
                  } else {
                    navigate(item.path);
                  }
                }}
                aria-current={isActive ? 'page' : undefined}
                sx={navButtonSx(isActive)}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '32px', filter: isActive ? `drop-shadow(0 0 5px ${INK.cyan}99)` : 'none' }}>
                  <Icon style={{ width: '20px', height: '20px' }} />
                </ListItemIcon>
                <Txt component="span" sx={{ fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, color: 'inherit' }}>
                  {item.label}
                </Txt>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <List sx={{ px: 1.5, mb: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              color: INK.muted,
              borderRadius: '8px',
              minHeight: 40,
              pl: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,23,68,0.08)',
                color: INK.red,
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: '32px' }}>
              <LogoutIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <Txt component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'inherit' }}>
              Logout
            </Txt>
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <AIBarProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh', background: 'var(--color-bg-primary)' }}>
        <Box component="nav" sx={{ width: { md: 240 }, flexShrink: { md: 0 } }}>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box',
              background: INK.ground,
              borderRight: '1px solid rgba(0,224,255,0.12)',
              borderRadius: '0 !important',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            // Above the fixed header so the drawer's own logo row shows
            zIndex: (t) => t.zIndex.drawer + 2,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              background: INK.ground,
              borderRight: '1px solid rgba(0,224,255,0.2)',
              borderRadius: '0 !important',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pb: isMobile ? '128px' : 0, // Bottom nav (68px) + AI bar (60px) = 128px
        }}
      >
        <OfflineBanner />
        <EmailVerificationBanner />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: 'rgba(10, 14, 20, 0.92)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(0,224,255,0.12)',
            width: { md: 'calc(100% - 240px)' },
            ml: { md: '240px' },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderRadius: 0,
            // global.css gives every Paper (AppBar included) the 12px card radius + border
            '&&': { borderRadius: '0 !important', borderTop: 'none !important', borderLeft: 'none !important', borderRight: 'none !important' },
          }}
        >
          <Toolbar sx={{ minHeight: '64px' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.5, display: { md: 'none' }, color: INK.cyan }}
            >
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
            
            {/* FuelTron Branding - Left side */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mr: { xs: 0.5, sm: 1 },
              height: '100%',
              flexShrink: 0, // never let the wordmark clip to "FUELTRO" on narrow phones
              minWidth: { xs: 'auto', sm: 'auto' }
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '28px'
              }}>
                <Box
                  component="img"
                  src="/FuelTron.png"
                  alt="FuelTron Logo"
                  sx={{
                    width: 30,
                    height: 30,
                    mr: 0.75,
                    borderRadius: '50%',
                    filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))',
                    border: '1px solid rgba(0,224,255,0.3)',
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontFamily: MONO,
                    fontWeight: 800,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    letterSpacing: 2.5,
                    textTransform: 'uppercase',
                    background: 'linear-gradient(135deg, #00E0FF, #00E676)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: 'none', // gradient handles the glow via filter
                    filter: 'drop-shadow(0 0 8px rgba(0,224,255,0.3))',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    pr: '3px', // room for the trailing letter-spacing so the "N" glow isn't clipped
                  }}
                >
                  FuelTron
                </Box>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />



            {/* Page Title - Show on larger screens as secondary info */}
            <Txt
              component="span"
              sx={{
                display: { xs: 'none', lg: 'block' },
                fontFamily: MONO,
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: INK.muted,
                mr: 3
              }}
            >
              {getCurrentPageTitle()}
            </Txt>
            
            {/* Right side buttons */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1.5 },
              height: '100%'
            }}>
              <IconButton
                onClick={() => openExternalLink(externalLinks.featureGuide('header-help'))}
                title="Feature guide"
                sx={{
                  // Phones: the feature guide lives in Settings; the header room goes to Coach (2026-11-03).
                  display: { xs: 'none', md: 'inline-flex' },
                  color: INK.muted,
                  '&:hover': { color: INK.cyan },
                  p: isMobile ? 0.5 : 1, minWidth: 40, minHeight: 40,
                  '& svg': { fontSize: 22 },
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
              {/* Coach moved here from the tab bar when Labs took its tab (2026-11-03). */}
              <IconButton
                onClick={() => navigate('/coach')}
                title="Coach"
                aria-label="Coach"
                sx={{
                  color: location.pathname.startsWith('/coach') ? INK.cyan : INK.muted,
                  '&:hover': { color: INK.cyan },
                  p: isMobile ? 0.5 : 1, minWidth: 40, minHeight: 40,
                }}
              >
                <ChatBubbleBottomCenterTextIcon style={{ width: 22, height: 22 }} />
              </IconButton>
              {/* Friends sits in the header now that Analysis has a tab (2026-11-03). The badge is
                  pending friend requests, same signal the old Friends tab pulsed for. */}
              <IconButton
                onClick={() => navigate('/friends')}
                title="Friends"
                aria-label={pendingFriendRequests > 0 ? `Friends, ${pendingFriendRequests} new` : 'Friends'}
                sx={{
                  color: location.pathname.startsWith('/friends') ? INK.cyan : INK.muted,
                  '&:hover': { color: INK.cyan },
                  p: isMobile ? 0.5 : 1, minWidth: 40, minHeight: 40,
                }}
              >
                <Badge
                  badgeContent={pendingFriendRequests > 0 ? pendingFriendRequests : null}
                  max={9}
                  sx={{ '& .MuiBadge-badge': { bgcolor: '#CE93D8', color: '#0a0e14', fontFamily: 'var(--font-mono)', fontSize: '0.6rem', fontWeight: 800, minWidth: 16, height: 16 } }}
                >
                  <UsersIcon style={{ width: 22, height: 22 }} />
                </Badge>
              </IconButton>
              <IconButton
                onClick={() => {
                  setNotificationDrawerOpen(true);
                  // Clear badge immediately on drawer open — standard social UX.
                  // "I've seen the list" = "I know what's new."
                  if (unreadCount > 0) markAllAsRead();
                }}
                title="Notifications"
                aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} new` : 'Notifications'}
                sx={{
                  color: notificationDrawerOpen ? INK.cyan : INK.muted,
                  '&:hover': { color: INK.cyan },
                  p: isMobile ? 0.5 : 1, minWidth: 40, minHeight: 40,
                  '& > .MuiBadge-root > svg': { fontSize: 22 },
                }}
              >
                <Badge
                  badgeContent={unreadCount > 0 ? unreadCount : null}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: INK.cyan,
                      color: INK.ground,
                      fontFamily: MONO,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      minWidth: 16,
                      height: 16,
                      boxShadow: `0 0 8px ${INK.cyan}99`,
                    },
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton onClick={(e) => {
                setProfileMenuAnchor(profileMenuAnchor ? null : e.currentTarget);
              }} sx={{ p: isMobile ? 0.5 : 1 }}>
                <Avatar
                  src={(user as any)?.profilePicture?.url}
                  sx={{
                    width: 30,
                    height: 30,
                    backgroundColor: 'rgba(0,224,255,0.12)',
                    color: INK.cyan,
                    fontFamily: MONO,
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    border: `1.5px solid ${INK.cyan}`,
                    boxShadow: `0 0 8px ${INK.cyan}55`,
                    '&:hover': {
                      backgroundColor: 'rgba(0,224,255,0.2)',
                    },
                    transition: 'background-color var(--transition-fast)',
                  }}
                >
                  {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        
        {/* Centered Content Area */}
        <Box
            component="div"
            sx={{
                flexGrow: 1,
                pt: { xs: 1, sm: 1.5, md: 2 },
                px: { xs: 1, sm: 3, md: 4 },
                pb: { xs: 2, sm: 3, md: 4 },
                mt: { xs: '56px', sm: '64px' }, // Header height
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
            }}
            >
            <Box sx={{ width: '100%', maxWidth: '1440px' }}>
                {showSetupBar && (
                  <Box
                    onClick={() => navigate('/activation')}
                    className="icon-text-row"
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      mt: { xs: 1, sm: 1.5 }, mb: 1.5, px: 1.5, minHeight: 40,
                      borderRadius: 1.5, cursor: 'pointer',
                      border: '1px solid rgba(0,224,255,0.4)',
                      background: 'rgba(0,224,255,0.08)',
                      boxShadow: '0 0 12px rgba(0,224,255,0.12)',
                      '&:hover': { background: 'rgba(0,224,255,0.14)' },
                    }}
                  >
                    <ArrowBackIcon sx={{ color: INK.cyan, fontSize: 18, filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.5))' }} />
                    <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, color: INK.cyan, letterSpacing: 2 }}>
                      BACK TO SETUP
                    </Txt>
                  </Box>
                )}
                {children || <Outlet />}
            </Box>
        </Box>
      </Box>

      {/* Custom positioned menu - only show when profileMenuAnchor exists */}
      {Boolean(profileMenuAnchor) && (
        <Box
          data-custom-profile-menu
          sx={{
            position: 'fixed',
            top: profileMenuAnchor ? `${profileMenuAnchor.getBoundingClientRect().bottom + 8}px` : '64px',
            right: profileMenuAnchor ? `${window.innerWidth - profileMenuAnchor.getBoundingClientRect().right}px` : '16px',
            zIndex: 9999,
            backgroundColor: 'var(--bg-card)',
            border: '1px solid rgba(0,224,255,0.25)',
            borderRadius: 1.5,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 18px rgba(0,224,255,0.06)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <List sx={{ p: 1 }}>
            {[
              { label: 'Profile & Settings', icon: <PersonIcon fontSize="small" />, path: '/profile' },
              { label: 'Privacy Policy', icon: <PolicyIcon fontSize="small" />, path: '/privacy' },
              { label: 'Terms of Service', icon: <TermsIcon fontSize="small" />, path: '/terms' },
            ].map(item => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isNavigating) return;
                    setIsNavigating(true);
                    navigate(item.path);
                    setTimeout(() => { setProfileMenuAnchor(null); setIsNavigating(false); }, 100);
                  }}
                  sx={{ borderRadius: '6px', minHeight: 40, color: INK.soft, '&:hover': { color: INK.text, background: 'rgba(0,224,255,0.06)' } }}
                >
                  <ListItemIcon sx={{ minWidth: '32px', color: INK.cyan }}>{item.icon}</ListItemIcon>
                  <Txt component="span" sx={{ fontSize: '0.8rem', color: 'inherit' }}>{item.label}</Txt>
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ my: 0.5, borderColor: INK.faint }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{ borderRadius: '6px', minHeight: 40, color: INK.muted, '&:hover': { color: INK.red, background: 'rgba(255,23,68,0.08)' } }}
              >
                <ListItemIcon sx={{ minWidth: '32px', color: 'inherit' }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <Txt component="span" sx={{ fontSize: '0.8rem', color: 'inherit' }}>Logout</Txt>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}
      
      {isMobile && <BottomNavigation />}

      {/* Invisible tutorial anchors for desktop to ensure Joyride steps exist */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }}>
        <span data-tour="bottom-nav" />
        <span data-tour="supplements" />
        <span data-tour="bloodwork" />
        <span data-tour="analysis" />
      </Box>

      {/* Persistent AI Input Bar */}
      <PersistentAIBar />

      {/* Notification Center Drawer */}
      <NotificationCenter
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
      />

      {/* Post-trial soft paywall — dismissible, shown once after Free Pro Day ends */}
      {showTrialPaywall && (
        <TrialPaywall
          firstName={user?.firstName}
          onStartTrial={() => { dismissTrialPaywall(); navigate('/upgrade'); }}
          onSkip={dismissTrialPaywall}
        />
      )}

      {/* iOS widget + Siri discovery (no-op on non-iOS) */}
      <WidgetDiscoveryDialog
        open={widgetDiscovery.widgetDialogOpen}
        onClose={widgetDiscovery.closeWidgetDialog}
      />
      <Snackbar
        open={widgetDiscovery.siriTipOpen}
        onClose={widgetDiscovery.dismissSiriTip}
        autoHideDuration={9000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 140, lg: 80 } }}
      >
        <Box
          onClick={widgetDiscovery.dismissSiriTip}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            background: 'rgba(10,14,20,0.95)',
            border: '1px solid rgba(206,147,216,0.4)',
            borderRadius: 1.5,
            boxShadow: '0 0 16px rgba(206,147,216,0.15)',
            cursor: 'pointer',
            maxWidth: 360,
          }}
        >
          <TipIcon sx={{ fontSize: 18, color: INK.purple, filter: `drop-shadow(0 0 6px ${INK.purple}99)` }} />
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                fontFamily: MONO,
                fontSize: '0.6rem',
                fontWeight: 700,
                color: INK.purple,
                letterSpacing: 1.5,
                mb: 0.25,
              }}
            >
              SIRI TIP
            </Box>
            <Box sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
              Try saying <strong>"Hey Siri, scan a meal with FuelTron"</strong>
            </Box>
          </Box>
        </Box>
      </Snackbar>
    </Box>
    </AIBarProvider>
  );
};